import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { NT_BOOKS } from './bibleBooks';
import styles from './BiblePassagePicker.module.scss';

interface BiblePassagePickerProps {
	books: string[];
	activeIndex: number;
	onSelect: (book: string) => void;
	anchorRect: DOMRect;
}

const SCROLL_THRESHOLD_PX = 8;

export const BiblePassagePicker: React.FC<BiblePassagePickerProps> = ({
	books,
	activeIndex,
	onSelect,
	anchorRect,
}) => {
	const listRef = useRef<HTMLUListElement>(null);
	const touchStartY = useRef<number>(0);

	useEffect(() => {
		const item = listRef.current?.querySelectorAll('[data-selectable]')[activeIndex] as
			| HTMLElement
			| undefined;
		item?.scrollIntoView({ block: 'nearest' });
	}, [activeIndex]);

	if (books.length === 0) return null;

	// Split into OT and NT groups, preserving original index for activeIndex matching
	const otBooks = books.map((book, i) => ({ book, i })).filter(({ book }) => !NT_BOOKS.has(book));
	const ntBooks = books.map((book, i) => ({ book, i })).filter(({ book }) => NT_BOOKS.has(book));

	const safeAreaTop = parseInt(
		getComputedStyle(document.documentElement).getPropertyValue(
			'--safe-area-inset-top',
		) || '0',
		10,
	);
	const spaceAbove = anchorRect.top - safeAreaTop - 8;
	const style: React.CSSProperties = {
		position: 'fixed',
		left: anchorRect.left,
		width: anchorRect.width,
		bottom: window.innerHeight - anchorRect.top + 4,
		maxHeight: spaceAbove,
		overflowY: 'auto',
		zIndex: 9999,
	};

	const renderBook = ({ book, i }: { book: string; i: number }) => (
		<li
			key={book}
			data-selectable
			className={[
				styles['bible-passage-picker__item'],
				i === activeIndex ? styles['bible-passage-picker__item--active'] : '',
			]
				.filter(Boolean)
				.join(' ')}
			onMouseDown={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onSelect(book);
			}}
			onTouchStart={(e) => {
				touchStartY.current = e.touches[0].clientY;
				e.stopPropagation();
				// No preventDefault — allows native scroll to work
			}}
			onTouchEnd={(e) => {
				const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
				if (dy < SCROLL_THRESHOLD_PX) {
					// Genuine tap — prevent ghost click and select
					e.preventDefault();
					e.stopPropagation();
					onSelect(book);
				}
			}}
		>
			<span className={styles['bible-passage-picker__name']}>{book}</span>
		</li>
	);

	return ReactDOM.createPortal(
		<div className={styles['bible-passage-picker']} style={style}>
			<ul className={styles['bible-passage-picker__list']} ref={listRef}>
				{otBooks.length > 0 && (
					<>
						<li className={styles['bible-passage-picker__section']}>
							Old Testament
						</li>
						{otBooks.map(renderBook)}
					</>
				)}
				{ntBooks.length > 0 && (
					<>
						<li className={styles['bible-passage-picker__section']}>
							New Testament
						</li>
						{ntBooks.map(renderBook)}
					</>
				)}
			</ul>
		</div>,
		document.body,
	);
};
